import { useEffect, useState } from "react";

import capitalize from "lodash/capitalize";
import { useIntl } from "react-intl";

import { AnimatedJiraTourProps } from "@/components/product-demos/animated-jira-tour/src/animated-jira-tour";
import { useLoggedInHasJira, useLoggedOut } from "@/components/user-context";
import { IndividualTabPropsCustom } from "@/components/wpl/tab-group/src";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";
import ImageChecklistSide from "@/patterns/image-checklist-side";
import { PaidHeroProps } from "@/patterns/paid-hero";
import { PatternThreeAlternatingImageTextProps } from "@/patterns/three-alternating-image-text/src";
import type {
    HeroProps,
    PaidLandingTypes
} from "@/templates/paid-landing/src/paid-landing.template";
import {
    PaidLandingProps,
    PaidLandingTemplate
} from "@/templates/paid-landing/src/paid-landing.template";
import { PaidLoggedInJiraProductTourOverview } from "@/templates/software/jira/logged-in/paid-logged-in/paid-logged-in-jira-product-tour-overview";
import {
    AnchorContentsResponse,
    PaidLandingPersonaTypes
} from "@/utils/anchor-queries/get-anchor-contents";
import { EnrollmentType, experiments, useExperiment } from "@/utils/experiment";
import { FeatureExposed } from "@/utils/feature-exposed";
import {
    JourneyLocalStoragePersona,
    useAnchorLocalStorage
} from "@/utils/local-storage-hook";

import { PaidLandingJiraAiTemplate } from "../../../../templates/paid-landing/src/cfind-4666-paid-landing-jira-ai.template";

interface HeroConfiguration extends Partial<HeroProps> {
    useNewHero: boolean;
    showAnimatedHero: boolean;
}

export type PaidLandingPageSporkVariants =
    | "pm"
    | "template"
    | "features"
    | "project_collaboration"
    | "automation"
    | "teamwork"
    | "agile"
    | "competitors"
    | "roadmaps"
    | "tracker"
    | "engineering"
    | "marketing"
    | "hr"
    | "legal"
    | "operations"
    | "design"
    | "programmgmt"
    | "sales";

export type AnchorPageProps = {
    persona: PaidLandingPersonaTypes;
    teamType?: JourneyLocalStoragePersona["team"];
    pageId: string;
    anchorContents?: AnchorContentsResponse | null;
    pageVariant: PaidLandingPageSporkVariants;
    animatedJiraTourProps?: AnimatedJiraTourProps;
};

/* eslint-disable complexity */
export const JiraSoftwarePagePaidLpJswAnchor = ({
    anchorContents,
    persona,
    teamType,
    pageId,
    pageVariant,
    animatedJiraTourProps
}: AnchorPageProps) => {
    const intl = useIntl();
    const productHref = useLocalizedHref("/software/jira");
    const [content, setContent] = useState<AnchorContentsResponse | undefined>(
        undefined
    );
    const loggedInHasJira = useLoggedInHasJira();
    const isLoggedOut = useLoggedOut();

    // Jira FFl experiment check start
    const jiraFflExperimentCheck = useExperiment(experiments.cfind4666JiraFfl);
    const isJiraFflVariant =
        jiraFflExperimentCheck.type === EnrollmentType.ENROLLED &&
        jiraFflExperimentCheck.value;

    // Jira FFl experiment check end

    const { setAnchorLocalStorage } = useAnchorLocalStorage();

    useEffect(() => {
        setAnchorLocalStorage({
            persona,
            teamTypeInferredMl: teamType,
            team: ""
        });
        setContent(anchorContents);
        // We only want to run this useEffect once.
    }, []);

    if (!content) {
        // TODO: loading UI for static renders
        return <div />;
    }

    const landingType: PaidLandingTypes =
        pageVariant === "features" || isTeamAnchor(pageVariant)
            ? "jira-software-rebranded-anchor"
            : "jira-software-anchor";

    const theme: PaidHeroProps["theme"] =
        pageVariant === "features" || isTeamAnchor(pageVariant)
            ? "dark-on-light"
            : "light-on-dark";

    const props: PaidLandingProps = {
        hideNavigationBar: false,
        customHeroSection: undefined,
        landingType: landingType,
        pageId,
        animatedJiraTourProps,
        showHorizontalRule: true,
        productNavProps: {
            productHref,
            productName: "jira",
            ariaLabel: intl.formatMessage({
                id: "wacJiraSoftwareNavigation.logo-label",
                defaultMessage: "Jira Software logo"
            }),
            dataProductKey: "jiraSoftware",
            showFreeHrefButton: false
        },
        heroProps: getHeroProps({
            resp: content,
            pageVariant,
            theme
        }),
        tabsProps: getTabProps(content, landingType),
        ctaProps: getCtaProps(content),
        testimonialProps: getTestimonialProps(content),
        alternatingTextAndImageProps: getAlternatingTextAndImageProps(content),
        hasRebrandedSignupFormStyles: true,
        heroBgColor: theme == "dark-on-light" ? "bgWhite" : "bgBlackBlue"
    };
    return loggedInHasJira ? (
        <FeatureExposed
            experimentKey="jfind-1504-after-d7-get-users-back-to-product"
            group="Test"
        >
            <FeatureExposed
                experimentKey="jfind-get-d1to6-users-back-to-product-for-paid"
                group="variant"
            >
                <FeatureExposed
                    experimentKey={"jfind-981-paid-jpto-logged-in-redesign"}
                    group={"Test"}
                >
                    <PaidLoggedInJiraProductTourOverview />
                </FeatureExposed>
            </FeatureExposed>
        </FeatureExposed>
    ) : isLoggedOut && isJiraFflVariant ? (
        <PaidLandingJiraAiTemplate {...props} />
    ) : (
        <PaidLandingTemplate {...props} />
    );
};

const heroFeatureMatrix: Record<string, HeroConfiguration> = {
    features: {
        useNewHero: true,
        showAnimatedHero: true,
        animatedSocialBanner: true,
        darkSolidTheme: false,
        showStaticImage: false
    },
    pm: {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: true,
        darkSolidTheme: true,
        showStaticImage: true
    },
    template: {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: true,
        darkSolidTheme: true,
        showStaticImage: true
    },
    project_collaboration: {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: true,
        darkSolidTheme: true,
        showStaticImage: false
    },
    automation: {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: true,
        darkSolidTheme: true,
        showStaticImage: false
    },
    teamwork: {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: true,
        darkSolidTheme: true,
        showStaticImage: false
    },
    agile: {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: true,
        darkSolidTheme: true,
        showStaticImage: false
    },
    competitors: {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: true,
        darkSolidTheme: true,
        showStaticImage: false
    },
    roadmaps: {
        useNewHero: true,
        showAnimatedHero: true,
        animatedSocialBanner: true,
        darkSolidTheme: true,
        showStaticImage: false
    },
    tracker: {
        useNewHero: true,
        showAnimatedHero: true,
        animatedSocialBanner: true,
        darkSolidTheme: true,
        showStaticImage: false
    },
    engineering: {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: true,
        darkSolidTheme: false,
        showStaticImage: false
    },
    marketing: {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: true,
        darkSolidTheme: false,
        showStaticImage: false
    },
    hr: {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: true,
        darkSolidTheme: false,
        showStaticImage: false
    },
    legal: {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: true,
        darkSolidTheme: false,
        showStaticImage: false
    },
    operations: {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: true,
        darkSolidTheme: false,
        showStaticImage: false
    },
    design: {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: true,
        darkSolidTheme: false,
        showStaticImage: false
    },
    programmgmt: {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: true,
        darkSolidTheme: false,
        showStaticImage: false
    },
    sales: {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: true,
        darkSolidTheme: false,
        showStaticImage: false
    }
};

const isTeamAnchor = (pageVariant: PaidLandingPageSporkVariants) => {
    const teamAnchors: PaidLandingPageSporkVariants[] = [
        "engineering",
        "marketing",
        "hr",
        "legal",
        "operations",
        "design",
        "programmgmt",
        "sales"
    ];
    return teamAnchors.includes(pageVariant);
};

const getHeroFeatures = (pageVariant: string): HeroConfiguration => {
    const defaultFeatures: HeroConfiguration = {
        useNewHero: false,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: false,
        showStaticImage: false
    };

    return heroFeatureMatrix[pageVariant] || defaultFeatures;
};

const getHeroProps = ({
    resp,
    pageVariant,
    theme
}: {
    resp: AnchorContentsResponse;
    pageVariant: PaidLandingPageSporkVariants;
    theme: PaidHeroProps["theme"];
    isUserLoggedIn?: boolean;
}): PaidLandingProps["heroProps"] => {
    const defaultProps: PaidLandingProps["heroProps"] = {
        theme: theme,
        headerText: "",
        secondaryText: "",
        signup: {
            bundle: "jira-software",
            edition: "free",
            featureFlags: {
                enableDefaultExpandedEmail: false,
                enableDeferredFullName: true
            }
        }
    };
    if (!resp || !resp[0] || !resp[0].node) {
        return defaultProps;
    }
    const { headline } = resp[0].node;
    const data = headline && headline[0] ? headline[0] : null;

    const planBenefits =
        data?.planBenefits && data?.planBenefits[0]
            ? data?.planBenefits[0]
            : null;
    const supportingAsset = planBenefits?.supportingAsset;
    const features = getHeroFeatures(pageVariant);

    const getImage = (url?: string | null, description?: string | null) =>
        url && description
            ? {
                  src: url ? url : "",
                  alt: description || ""
              }
            : undefined;

    const getFeaturesRawHtml = () =>
        planBenefits &&
        planBenefits.planFeatures &&
        planBenefits.planFeatures[0]
            ? planBenefits.planFeatures[0].description || ""
            : "";

    return {
        ...defaultProps,
        ...features,
        headerText: data?.name || "",
        secondaryText: data?.subheading || "",
        image: getImage(supportingAsset?.url, supportingAsset?.description),
        featuresRawHtml: getFeaturesRawHtml(),
        tertiaryText: planBenefits?.description || ""
    };
};

const getTabProps = (
    resp: AnchorContentsResponse,
    landingType: PaidLandingTypes
): PaidLandingProps["tabsProps"] => {
    const defaultProps: PaidLandingProps["tabsProps"] = {
        dataProductKey: "jiraSoftware-PaidLpJiraPm",
        headerText: "",
        tabStyles: {
            defaultBG: wacToken("color.neutral.light0"),
            // eslint-disable-next-line local/ensure-wac-token-usage
            defaultText: "#091e42",
            hoverBG: wacToken("color.neutral.light0"),
            // eslint-disable-next-line local/ensure-wac-token-usage
            hoverText: "#091e42",
            // eslint-disable-next-line local/ensure-wac-token-usage
            activeBG: "#deebff",
            // eslint-disable-next-line local/ensure-wac-token-usage
            activeText: "#091e42"
        },
        styles: {
            "--lp-tabs-hover": "#deebff",
            "--lp-tabs-unselected": "#6b778c"
        },
        tabs: undefined
    };

    if (!resp || !resp[0] || !resp[0].node) {
        return defaultProps;
    }

    const tabs = getTabs(resp);
    const primaryMessage = getPrimaryMessage(resp);

    return {
        ...defaultProps,
        landingType: landingType,
        headerText: primaryMessage?.name || "",
        tabs
    };
};

const getCtaProps = (
    resp: AnchorContentsResponse
): PaidLandingProps["ctaProps"] => {
    const cta = getCta(resp);
    return {
        headerText: cta?.valueProposition || "",
        ctaProps: {
            buttonProps: {
                color: "yellow",
                url: cta?.url || "",
                text: cta?.text || "",
                dataEventProps: {
                    "data-event": "clicked",
                    "data-event-container": "ctaSection",
                    "data-event-component": "linkButton",
                    "data-product-key": "jiraSoftware"
                }
            }
        },
        backgroundProperties: {
            textColor: wacToken("color.neutral.light0")
        }
    };
};

const getTestimonialProps = (
    resp: AnchorContentsResponse
): PaidLandingProps["testimonialProps"] | undefined => {
    if (!resp || !resp[0] || !resp[0].node) {
        return undefined;
    }
    const { socialProof } = resp[0].node;
    const data = socialProof && socialProof[0] ? socialProof[0] : null;

    if (!data) {
        return undefined;
    }

    return {
        headerText: data?.name,
        logos: data?.customers?.map((customer) => {
            return {
                src:
                    customer &&
                    customer.logo &&
                    customer?.logo[0] &&
                    customer?.logo[0].url
                        ? customer?.logo[0].url
                        : "",
                alt:
                    customer &&
                    customer.logo &&
                    customer?.logo[0] &&
                    customer?.logo[0].description
                        ? customer?.logo[0].description
                        : "",
                lazy: true
            };
        })
    };
};

const getAlternatingTextAndImageProps = (
    resp: AnchorContentsResponse
): PatternThreeAlternatingImageTextProps | undefined => {
    const results = getResults(resp);
    function generateProps(
        result: {
            asset: {
                contentType: string | null;
                fileName: string | null;
                title: string | null;
                details: string | null;
                description: string | null;
                url: string | null;
            } | null;
            subheading: string | null;
            name: string | null;
            id: string;
        } | null,
        index: number
    ) {
        // this shouldn't go past
        const countMap = ["one", "two", "three", "four"];
        const count = capitalize(`${countMap[index]}`);
        const position = index % 2 === 0 ? "left" : "right";

        return {
            [`row${count}Header`]: result?.name || "",
            [`row${count}Body`]: result?.subheading || "",
            [`row${count}ImageLink`]: result?.asset?.url,
            [`row${count}ImageAltText`]: result?.asset?.description || "",
            [`row${count}ImagePosition`]: position
        };
    }

    const contentBlocks = results
        ?.map((result, index) => generateProps(result, index))
        .slice(0, 4) // ensure max of 4 results so we don't break the pattern
        .filter((result) => !!result)
        .reduce((acc, curr) => {
            return {
                ...acc,
                ...curr
            };
        });

    return {
        imageWidth: "100%",
        imageTextSideRowOnePaddingBelowLargeScreen: "32px 0 16px",
        imageTextSideRowTwoPadding: "16px 0",
        imageTextSideRowThreePaddingBelowLargeScreen: "16px 0 32px",
        ...contentBlocks
    };
};

const getPrimaryMessage = (resp: AnchorContentsResponse) => {
    if (!resp || !resp[0] || !resp[0].node) {
        return null;
    }
    const { primaryMessage } = resp[0].node;
    const data = primaryMessage && primaryMessage[0] ? primaryMessage[0] : null;

    return data;
};

const getTabs = (
    resp: AnchorContentsResponse
): IndividualTabPropsCustom[] | undefined => {
    const primaryMessage = getPrimaryMessage(resp);

    if (!primaryMessage || !primaryMessage?.supportingExample) {
        return undefined;
    }

    return primaryMessage.supportingExample
        .filter((tab) => tab !== null)

        .map((tab) => {
            const supportingAsset = tab?.supportingAsset;
            const advocateQuote = tab?.advocateQuote;

            return {
                textLabel: tab?.name || "",
                id: tab?.name || "",
                component: (
                    <ImageChecklistSide
                        isShowTestimonyBorder={false}
                        isIncludeTestimony={!!tab?.advocateQuote}
                        testimonyIsUsingTextQuotationMarks={true}
                        key={tab?.name}
                        title={tab?.heading || ""}
                        rawHtmlContent={tab?.proofpoint || ""}
                        imageSrc={supportingAsset?.url || ""}
                        imageAlt={supportingAsset?.description || ""}
                        quoteBlurb={advocateQuote?.advocateQuote || ""}
                        quoteName={advocateQuote?.advocateName || ""}
                        quoteCompany={advocateQuote?.advocateJobTitle || ""}
                        testimonyImageSrc={
                            advocateQuote?.advocateHeadshot?.url || ""
                        }
                        testimonyImageAlt={
                            advocateQuote?.advocateHeadshot?.description || ""
                        }
                    />
                )
            };
        });
};

const getResults = (resp: AnchorContentsResponse) => {
    if (!resp || !resp[0] || !resp[0].node) {
        return null;
    }
    const { results } = resp[0].node;
    const data = results && results.length ? results : null;

    return data;
};

const getCta = (resp: AnchorContentsResponse) => {
    if (!resp || !resp[0] || !resp[0].node) {
        return null;
    }
    const { callToAction } = resp[0].node;
    const data = callToAction && callToAction[0] ? callToAction[0] : null;

    return data;
};
